/**
 * Inspect window (plan 23 row 34, lane BM) — registry id `inspect`, opened by
 * the target frame's Inspect button (inspectTarget.ts). On open, and each
 * time the target changes, it sends `inspectPlayer` and renders the server's
 * `inspectResult`: the other player's public sheet. Read-only; worn items
 * show the shared item tooltip (lane BI) on hover, compared to what you wear.
 * WindowHost draws it in the Tome frame (Tome UI Round 5, E4), whose panel
 * carries the zone's --tome-* tokens; a sheet whose session id is yours is
 * marked as your own.
 */
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  INSPECT_REQUEST_MESSAGE,
  INSPECT_RESULT_MESSAGE,
  type InspectResult,
} from '../../../../../shared/social/socialWire';
import { useColyseusMessages } from '../../../hooks/useColyseusMessages';
import { useOptionalGame } from '../../../contexts/GameContext';
import { getInspectTarget, subscribeInspectTarget } from './inspectTarget';
import { InspectSheetView } from './InspectSheetView';
import '../SocialTome.css';
import './InspectTome.css';

let nextRequestId = 1;

const FAILURE_COPY: Record<string, string> = {
  not_found: 'That player is no longer here to inspect.',
  rate_limited: 'Too quick — try again in a moment.',
  invalid: 'That player cannot be inspected.',
};

export const InspectContent: React.FC = () => {
  const target = useSyncExternalStore(subscribeInspectTarget, getInspectTarget, getInspectTarget);
  const [result, setResult] = useState<InspectResult | null>(null);
  const pending = useRef(0);
  const selfId = useOptionalGame()?.player?.id;

  // Bound before the request below is sent (effects run in order).
  useColyseusMessages({
    [INSPECT_RESULT_MESSAGE]: (message: InspectResult) => {
      if (message?.requestId === pending.current) setResult(message);
    },
  });

  useEffect(() => {
    setResult(null);
    if (!target) return;
    const requestId = nextRequestId++;
    pending.current = requestId;
    const nm = (globalThis as { nm?: { send?: (type: string, message: unknown) => void } }).nm;
    nm?.send?.(INSPECT_REQUEST_MESSAGE, { sessionId: target.sessionId, requestId });
  }, [target]);

  return (
    <div className="inspect-tome" data-testid="inspect-content">
      {!target && <p className="social-tome-empty">Target another player and press Inspect.</p>}
      {target && !result && <p className="social-tome-empty" role="status">Looking at {target.name}…</p>}
      {result && !result.ok && <p className="social-tome-empty" role="status">{FAILURE_COPY[result.reason] ?? FAILURE_COPY.invalid}</p>}
      {result?.ok && <InspectSheetView sheet={result.sheet} self={!!selfId && selfId === target?.sessionId} />}
    </div>
  );
};
